/* Replacement for MenuOwner that inverts control, Components that would formerly use MenuOwner
 * as a mixin should instead include <Menu> as a child component
*	 example use -
*	 export default menuOwnerHOC(MyComponent)
*  This implementation will set isMenuOpen as a prop on the wrapped component
*  instead of setting  as the mixin would
*/

import React from 'react'
import T from 'prop-types'
import ReactDOM from 'react-dom'
import { findAncestorBySelector } from '../utils.js'
import MenuOwnerPortal from './MenuOwnerPortal'
import { generateId } from '../utils'

const displayNoneStyle = {display:'none'}
const stopPropagation = e => e.stopPropagation()

export default class MenuOwner extends React.Component {
  static displayName = 'MenuOwner'
  static propTypes = {
    className: T.string,
    children: T.node,
    isMenuOpen: T.bool,
    renderMenu: T.func.isRequired, // Should return rendered menu contents component
    onClose: T.func.isRequired, // Called when menu is requesting to be closed
    getMenuParentNode: T.func,
    getMenuAlignNode: T.func,
    getMenuPosition: T.func, // (menuRect) // Passed menu boundingClientRect dimensions
    menuAnchor: T.string, // e.g. top-left
    menuFullWidth: T.bool,
    style: T.object,
    elementType: T.oneOf(['div', 'span'])
  }

  static defaultProps = {
    isMenuOpen: false,
    elementType: 'div',
    menuFullWidth: false
  }

  _id = generateId('menuowner')
  _menuId = this._id + '_menu'

  componentWillUnmount() {
    this.handleMenuDetached()
  }

  // Determine whether a node is within this component's element, its menu's element,
  // or the menu elements for any child MenuOwner components.
  isNodeInMenuHierarchy = node => {
    if (node) {
      // is this the menu owner
      if (
        findAncestorBySelector(node, '#' + this._id) ||
        findAncestorBySelector(node, '#' + this._menuId)
      ) {
        return true
      }
      // is the menu owner a child
      const menuEl = findAncestorBySelector(node, `[data-menuownerid]`)
      const menuOwnerEl =
        menuEl &&
        document.querySelector(
          '#' + menuEl.getAttribute('data-menuownerid')
        )
      if (menuOwnerEl && this.isNodeInMenuHierarchy(menuOwnerEl)) {
        return true
      }
    }
    return false
  }

  _getMenuAlignNode = () => {
    const { getMenuAlignNode } = this.props
    return getMenuAlignNode ? getMenuAlignNode() : ReactDOM.findDOMNode(this)
  }

  _alignMenu = () => {
    const { getMenuPosition } = this.props
    if (!(this._menuRef != null)) {
      return
    }
    let x, y, isAbove
    const menuNode = ReactDOM.findDOMNode(this._menuRef)

    if (menuNode) {
      const menuRect = menuNode.getBoundingClientRect()
      if (getMenuPosition) {
        [x, y] = getMenuPosition(menuRect)
      } else {
        const alignRect = this._getMenuAlignNode().getBoundingClientRect()
        const anchor = this.props.menuAnchor || 'bottom-left'
        const [vAlign, hAlign] = anchor.split('-')
        if (vAlign === 'bottom') {
          y = alignRect.bottom
          isAbove = false
          if (y + menuRect.height > window.innerHeight) {
            y = alignRect.top - menuRect.height
            isAbove = true
          }
        } else if (vAlign === 'top') {
          y = alignRect.top - menuRect.height
          isAbove = true
          if (y < 0) {
            y = alignRect.bottom
            isAbove = false
          }
        } else {
          throw new Error('Invalid menu vertical alignment: ' + vAlign)
        }
        if (hAlign === 'left') {
          x = alignRect.left
          if (x + menuRect.width > window.innerWidth) {
            x = alignRect.right - menuRect.width
          }
        } else if (hAlign === 'right') {
          x = alignRect.right - menuRect.width
          if (x < 0) {
            x = alignRect.left
          }
        } else {
          throw new Error('Invalid menu horizontal alignment: ' + vAlign)
        }
        menuNode.classList[isAbove ? 'add' : 'remove']('dropup')
        if (this.props.menuFullWidth) {
          menuNode.style.width = alignRect.width + 'px'
        }
      }
      menuNode.style.left = x + 'px'
      menuNode.style.top = y + 'px'
    }
  }

  _onMenuCloserEvent = e => {
    const { onClose } = this.props
    if (!this.isNodeInMenuHierarchy(e.target)) {
      onClose()
    }
  }

  // Bind the global events for closing the menu
  _bindGlobalEvents = () => {
    if (!this._globalEventsBound) {
      window.addEventListener('mousedown', this._onMenuCloserEvent)
      window.addEventListener('touchstart', this._onMenuCloserEvent)
      window.addEventListener('scroll', this._onMenuCloserEvent)
      window.addEventListener('mousewheel', this._onMenuCloserEvent)
      window.addEventListener('resize', this._onMenuCloserEvent)
      window.addEventListener('transitionend', this._alignMenu)
      window.addEventListener('webkitTransitionEnd', this._alignMenu)
      this._globalEventsBound = true
    }
  }

  _unbindGlobalEvents = () => {
    if (this._globalEventsBound) {
      window.removeEventListener('mousedown', this._onMenuCloserEvent)
      window.removeEventListener('touchstart', this._onMenuCloserEvent)
      window.removeEventListener('scroll', this._onMenuCloserEvent)
      window.removeEventListener('mousewheel', this._onMenuCloserEvent)
      window.removeEventListener('resize', this._onMenuCloserEvent)
      window.removeEventListener('transitionend', this._alignMenu)
      window.removeEventListener('webkitTransitionEnd', this._alignMenu)
      delete this._globalEventsBound
    }
  }

  handleMenuRendered = () => {
    this._bindGlobalEvents()
    // Queue up an alignment on the next tick
    cancelAnimationFrame(this._alignMenuRAF)
    this._alignMenuRAF = requestAnimationFrame(this._alignMenu)
  }

  handleMenuDetached = () => {
    cancelAnimationFrame(this._alignMenuRAF)
    this._unbindGlobalEvents()
    delete this._menuRef
  }

  handleMenuRef = rootMenuEl => {
    this._menuRef = rootMenuEl
  }

  render() {
    const {
      children,
      className,
      style,
      elementType,
      isMenuOpen,
      renderMenu
    } = this.props

    const Comp = elementType
    const menuId = this._menuId

    return (
      <Comp
        className={`menu_owner ${className || ''}`}
        style={style}
        id={this._id}
        aria-haspopup={isMenuOpen ? 'menu' : false}
        aria-owns={isMenuOpen ? menuId : null} //maybe aria-controls?
        aria-expanded={isMenuOpen}
      >
        {children}
        {isMenuOpen &&
          // Wrap the portal within an element that intercepts and prevents bubbling of mouse
          // events. This prevents issues with code that relied on the old pre-portals behavior
          // where events did not bubble from one DOM tree to the other.
          <div
            style={displayNoneStyle}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onMouseUp={stopPropagation}
          >
            <MenuOwnerPortal
              menuOwnerId={this._id}
              menuId={menuId}
              ref={this.handleMenuRef}
              onRendered={this.handleMenuRendered}
              onDetached={this.handleMenuDetached}
              alignSelf={this._alignMenu}
            >{renderMenu()}</MenuOwnerPortal>
          </div>
        }
      </Comp>
    )
  }
}
